import { createSelector } from "@reduxjs/toolkit";
import { withinCallCenterHours } from "utils/time/withinCallCenterHours";

import {
	getFacilityWithUnits,
	getFacilityCardProps
} from "store/selectors/entities/facility";
import formatPageTypeTitle from "utils/search/formatPageTypeTitle";
import buildSisterPages from "utils/search/buildSisterPages";
// Search Page Meta

export const selectSearchPageMeta = createSelector(
	(state) => state.search.cityUrl,
	(state) => state.search.meta,
	(state) => state.app.baseUrl,
	(state) => state.search.pageText,
	(state) => state.search.nearbyCities,
	(state) => state.search.location,
	(state) => state.search.pageType,
	(state) => state.search.headline,
	(state) => state.search.subPageType,
	(state) => state.search.stateUrl,
	(state) => state.search.staticMapUrl,
	(
		cityUrl,
		meta,
		baseUrl,
		pageText,
		nearbyCities,
		location,
		pageType,
		headline,
		subPageType,
		stateUrl,
		staticMapUrl
	) => ({
		cityUrl,
		meta: {
			...meta,
			canonical: baseUrl + meta.canonical
		},
		pageText,
		nearbyCities,
		location,
		pageType,
		headline: headline || formatPageTypeTitle(subPageType),
		stateUrl,
		staticMapUrl
	})
);

export const selectSearchResultsPagination = createSelector(
	(state) => state.search.page,
	(state) => state.search.hasMorePages,
	(state) => state.search.totalListings,
	(currentPage, hasMorePages, totalListings) => ({
		currentPage,
		hasMorePages,
		totalListings
	})
);

export const selectSisterPages = createSelector(
	(state) => state.search.amenityPages,
	(state) => state.search.meta.canonicalUri,
	(amenityPages, canonicalUri) => ({
		amenityPages: buildSisterPages(amenityPages, canonicalUri)
	})
);

// Uses state.search.facilities (IDs for current search) so results replace on new search (e.g. map move), not append
export const selectFacilityCards = createSelector(
	({ search }) => search?.facilities || search?.listings || [],
	({ facilities }) => facilities.byId,
	({ units }) => units.byId,
	({ app }) => app?.baseUrl || "",
	(facilityIds, facilitiesById, unitsById, baseUrl) => {
		const ids = Array.isArray(facilityIds) ? facilityIds : [];
		const cards = ids
			.map((id) => getFacilityWithUnits(id, facilitiesById, unitsById))
			.map((f) => getFacilityCardProps(f))
			.filter((f) => f);

		if (!baseUrl) {
			return cards;
		}

		return cards.map((card) => {
			if (!card.schema || !card.schema.url) {
				return card;
			}

			const hasAbsoluteUrl = /^https?:\/\//i.test(card.schema.url);
			if (hasAbsoluteUrl) {
				return card;
			}

			return {
				...card,
				schema: {
					...card.schema,
					url: `${baseUrl}${card.schema.url}`
				}
			};
		});
	}
);

// Derived, environment-aware value. Returns null on SSR to avoid mismatches.
export const selectCallCenterOpen = () =>
	typeof globalThis.window === "undefined" ? null : withinCallCenterHours();

export const searchPageSelector = createSelector(
	selectSearchPageMeta,
	selectSearchResultsPagination,
	selectSisterPages,
	selectFacilityCards,
	selectCallCenterOpen,
	(
		searchPageMeta,
		searchResultsPagination,
		sisterPages,
		facilityCards,
		isCallCenterOpen
	) => ({
		...searchPageMeta,
		...searchResultsPagination,
		...sisterPages,
		facilityCards,
		isCallCenterOpen
	})
);

export default searchPageSelector;
